import { useState } from "react";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  UnderlineType,
  Footer,
  LevelFormat,
  convertInchesToTwip,
  SymbolRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
} from "docx";
import { Button } from "@/components/ui/button";
import { FileText } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import type { JSONContent } from "@tiptap/react";
import { Sentry } from "@/lib/sentry";

// Feature flag for DOCX RTL debug logging — set to false when done investigating
const DOCX_DEBUG = true;

const numbering = {
  config: [
    {
      reference: "my-crazy-numbering",
      levels: [
        {
          level: 0,
          format: LevelFormat.DECIMAL,
          text: "%1.",
          alignment: AlignmentType.START,
          style: {
            paragraph: {
              indent: { left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.18) },
            },
          },
        },
        {
          level: 1,
          format: LevelFormat.DECIMAL,
          text: "%2.",
          alignment: AlignmentType.START,
          style: {
            paragraph: {
              indent: { left: convertInchesToTwip(1), hanging: convertInchesToTwip(0.68) },
            },
          },
        },
        {
          level: 2,
          format: LevelFormat.UPPER_ROMAN,
          text: "%3)",
          alignment: AlignmentType.START,
          style: {
            paragraph: {
              indent: { left: convertInchesToTwip(1.5), hanging: convertInchesToTwip(1.18) },
            },
          },
        },
        {
          level: 3,
          format: LevelFormat.LOWER_ROMAN,
          text: "%4)",
          alignment: AlignmentType.START,
          style: {
            paragraph: {
              indent: { left: 2880, hanging: 2420 },
            },
          },
        },
      ],
    },
    {
      reference: "my-unique-bullet-points",
      levels: [
        {
          level: 0,
          format: LevelFormat.BULLET,
          text: "\u2022",
          alignment: AlignmentType.LEFT,
          style: {
            paragraph: {
              indent: { left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.25) },
            },
          },
        },
        {
          level: 1,
          format: LevelFormat.BULLET,
          text: "\u006F",
          alignment: AlignmentType.LEFT,
          style: {
            paragraph: {
              indent: { left: convertInchesToTwip(1), hanging: convertInchesToTwip(0.25) },
            },
          },
        },
        {
          level: 2,
          format: LevelFormat.BULLET,
          text: "\u25A0",
          alignment: AlignmentType.LEFT,
          style: {
            paragraph: {
              indent: { left: 2160, hanging: convertInchesToTwip(0.25) },
            },
          },
        },
        {
          level: 3,
          format: LevelFormat.BULLET,
          text: "\u25A0",
          alignment: AlignmentType.LEFT,
          style: {
            paragraph: {
              indent: { left: 2880, hanging: convertInchesToTwip(0.25) },
            },
          },
        },
        {
          level: 4,
          format: LevelFormat.BULLET,
          text: "\u25A0",
          alignment: AlignmentType.LEFT,
          style: {
            paragraph: {
              indent: { left: 3600, hanging: convertInchesToTwip(0.25) },
            },
          },
        },
      ],
    },
  ],
};

// Language Detection Helpers
const isHebrewChar = (char: string): boolean => {
  const code = char.charCodeAt(0);
  return (
    (code >= 0x0590 && code <= 0x05FF) || // Hebrew block
    (code >= 0xFB1D && code <= 0xFB4F)    // Hebrew presentation forms
  );
};

const isLatinChar = (char: string): boolean => {
  const code = char.charCodeAt(0);
  return (
    (code >= 0x0020 && code <= 0x007F) || // Basic Latin
    (code >= 0x0080 && code <= 0x00FF)    // Latin-1 Supplement
  );
};

const detectLanguage = (char: string): 'hebrew' | 'latin' | 'neutral' => {
  if (isHebrewChar(char)) return 'hebrew';
  if (isLatinChar(char)) return 'latin';
  return 'neutral'; // Numbers, punctuation, symbols, spaces
};

/**
 * Splits mixed Hebrew/English text into language-specific segments.
 * Handles neutral characters (numbers, punctuation, spaces) by adopting
 * the context language from surrounding text.
 *
 * @param text - The mixed-language text to segment
 * @returns Array of segments with text and language code ('he-IL' or 'en-US')
 *
 * @example
 * splitTextByLanguage("Hello שלום 123")
 * // Returns: [
 * //   { text: "Hello ", lang: "en-US" },
 * //   { text: "שלום ", lang: "he-IL" },
 * //   { text: "123", lang: "he-IL" }
 * // ]
 */
const splitTextByLanguage = (
  text: string
): Array<{ text: string; lang: 'he-IL' | 'en-US' }> => {
  if (!text) return [{ text: '', lang: 'he-IL' }];

  const segments: Array<{ text: string; lang: 'he-IL' | 'en-US' }> = [];
  const currentSegmentChars: string[] = [];
  let currentLang: 'he-IL' | 'en-US' = 'he-IL';
  let lastDetectedLang: 'hebrew' | 'latin' | 'neutral' = 'neutral';

  for (const char of text) {
    const detectedLang = detectLanguage(char);

    // Initialize language on first non-neutral character
    if (lastDetectedLang === 'neutral' && detectedLang !== 'neutral') {
      currentLang = detectedLang === 'hebrew' ? 'he-IL' : 'en-US';
      lastDetectedLang = detectedLang;
      currentSegmentChars.push(char);
      continue;
    }

    // Check if we need to start a new segment
    const shouldStartNewSegment =
      detectedLang !== 'neutral' &&
      lastDetectedLang !== 'neutral' &&
      detectedLang !== lastDetectedLang;

    if (shouldStartNewSegment) {
      // Finalize current segment
      segments.push({
        text: currentSegmentChars.join(''),
        lang: currentLang,
      });
      // Start new segment
      currentSegmentChars.length = 0;
      currentLang = detectedLang === 'hebrew' ? 'he-IL' : 'en-US';
      lastDetectedLang = detectedLang;
      currentSegmentChars.push(char);
    } else {
      // Continue current segment
      currentSegmentChars.push(char);
      if (detectedLang !== 'neutral') {
        lastDetectedLang = detectedLang;
      }
    }
  }

  // Add final segment
  if (currentSegmentChars.length > 0) {
    segments.push({
      text: currentSegmentChars.join(''),
      lang: currentLang,
    });
  }

  if (DOCX_DEBUG) {
    console.log("[DOCX_DEBUG] splitTextByLanguage:", {
      input: text,
      segments: segments.map(s => ({ text: s.text, lang: s.lang })),
    });
  }

  return segments;
};

/**
 * Detects the dominant language of the entire document by scanning all text nodes
 * for the first alphabetic character. Returns 'he-IL' or 'en-US'.
 */
const detectDocumentLanguage = (editorState: JSONContent): 'he-IL' | 'en-US' => {
  const findFirstAlpha = (nodes: JSONContent[] | undefined): string | null => {
    if (!nodes) return null;
    for (const node of nodes) {
      if (node.type === "text" && node.text) {
        const match = node.text.match(/[a-zA-Z\u0590-\u05FF]/);
        if (match) return match[0];
      }
      if (node.content) {
        const found = findFirstAlpha(node.content);
        if (found) return found;
      }
    }
    return null;
  };

  const firstChar = findFirstAlpha(editorState.content);
  return firstChar && isHebrewChar(firstChar) ? 'he-IL' : 'en-US';
};

/**
 * Detects paragraph alignment and bidirectional settings based on first letter.
 * Searches through all text nodes in the content to find the first alphabetic character.
 */
const getParagraphDirection = (node: JSONContent) => {
  const findFirstLetter = (content: JSONContent[] | undefined): string | null => {
    if (!content) return null;

    for (const child of content) {
      if (child.type === "text" && child.text) {
        const match = child.text.match(/[a-zA-Z\u0590-\u05FF]/);
        if (match) return match[0];
      }
      // Recurse if nested content exists
      if (child.content) {
        const found = findFirstLetter(child.content);
        if (found) return found;
      }
    }
    return null;
  };

  const firstLetter = findFirstLetter(node.content);

  if (!firstLetter) {
    if (DOCX_DEBUG) {
      console.log("[DOCX_DEBUG] getParagraphDirection: no first letter found, defaulting to Hebrew RTL");
    }
    return { align: AlignmentType.RIGHT, bidi: true }; // Default to Hebrew
  }

  const isHebrew = isHebrewChar(firstLetter);
  const result = {
    align: isHebrew ? AlignmentType.START : AlignmentType.LEFT,
    bidi: isHebrew
  };

  if (DOCX_DEBUG) {
    console.log("[DOCX_DEBUG] getParagraphDirection:", {
      firstLetter,
      isHebrew,
      align: result.align,
      bidi: result.bidi,
    });
  }

  return result;
};

// TipTap text format handling
const getTextRunOptionsFromMarks = (marks: JSONContent["marks"] = []) => {
  const options: Record<string, string | boolean | object> = {};

  marks.forEach((mark) => {
    switch (mark.type) {
      case "bold":
        options.bold = true;
        break;
      case "italic":
        options.italics = true;
        break;
      case "underline":
        options.underline = {
          type: UnderlineType.SINGLE,
        };
        break;
      case "strike":
        options.strike = true;
        break;
      case "code":
        options.font = {
          name: "Courier New",
        };
        break;
    }
  });

  return options;
};

const createTextRuns = (
  text: string,
  marks: JSONContent["marks"] = []
): TextRun[] => {
  const formatOptions = getTextRunOptionsFromMarks(marks);
  const segments = splitTextByLanguage(text);

  if (DOCX_DEBUG) {
    console.log("[DOCX_DEBUG] createTextRuns:", {
      inputText: text,
      marks: marks?.map(m => m.type),
      segmentCount: segments.length,
      segments: segments.map(s => ({
        text: s.text,
        lang: s.lang,
        rtl: s.lang === 'he-IL',
      })),
    });
  }

  const isCodeFont = formatOptions.font && (formatOptions.font as { name: string }).name === "Courier New";

  return segments.map((segment) => {
    const isHebrew = segment.lang === 'he-IL';
    return new TextRun({
      text: segment.text,
      ...formatOptions,
      rightToLeft: isHebrew,
      language: {
        value: segment.lang,
        bidirectional: "he-IL",
      },
      // Use complex script hint for Hebrew unless overridden by code mark
      ...(!isCodeFont && {
        font: {
          hint: isHebrew ? "cs" : "default",
          name: "Arial",
        },
      }),
    });
  });
};

const getHeadingLevel = (tag: string): (typeof HeadingLevel)[keyof typeof HeadingLevel] => {
  switch (tag) {
    case "h1":
      return HeadingLevel.HEADING_1;
    case "h2":
      return HeadingLevel.HEADING_2;
    case "h3":
      return HeadingLevel.HEADING_3;
    case "h4":
      return HeadingLevel.HEADING_4;
    case "h5":
      return HeadingLevel.HEADING_5;
    case "h6":
      return HeadingLevel.HEADING_6;
    default:
      return HeadingLevel.HEADING_1;
  }
};

const DownloadWordButton = ({
  disabledFromParent,
  fileName,
  editorState,
}: {
  disabledFromParent: boolean;
  fileName: string;
  editorState: JSONContent;
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const renderTextRunsFromContent = (content: JSONContent[]): TextRun[] => {
    if (!Array.isArray(content)) {
      return [];
    }

    const textRuns: TextRun[] = [];

    content.forEach((node) => {
      if (node.type === "text") {
        const runs = createTextRuns(node.text || "", node.marks);
        textRuns.push(...runs);
      }
    });

    return textRuns;
  };

  const renderParagraphNode = (node: JSONContent): Paragraph => {
    const textRuns = renderTextRunsFromContent(node.content || []);
    const { align, bidi } = getParagraphDirection(node);

    return new Paragraph({
      children: textRuns,
      alignment: align,
      bidirectional: bidi,
      spacing: {
        after: 200, // 200 twips = ~3.5pt
      },
    });
  };

  const renderHeadingNode = (node: JSONContent): Paragraph => {
    const headingLevel = node.attrs?.level || 1;
    const textRuns = renderTextRunsFromContent(node.content || []);
    const { align, bidi } = getParagraphDirection(node);

    const paragraphConfig: Record<string, unknown> = {
      children: textRuns,
      heading: getHeadingLevel(`h${headingLevel}`),
      bidirectional: bidi,
      spacing: {
        before: 240, // 240 twips = ~4pt
        after: 200, // 200 twips = ~3.5pt
      },
    };

    // H1 headings stay centered, others use detected alignment
    if (headingLevel === 1) {
      paragraphConfig.alignment = AlignmentType.CENTER;
    } else {
      paragraphConfig.alignment = align;
    }

    return new Paragraph(paragraphConfig);
  };

  const renderListNode = (node: JSONContent, level: number = 0): Paragraph[] => {
    const listType = node.type as "orderedList" | "bulletList" | "taskList";
    const listItems = node.content || [];
    const paragraphs: Paragraph[] = [];

    // בחר את ההפניה הנכונה להגדרות ה-numbering שלך
    const listReference = listType === "orderedList" ? "my-crazy-numbering" : "my-unique-bullet-points";

    listItems.forEach((listItem) => {
      if ((listItem.type !== "listItem" && listItem.type !== "taskItem") || !listItem.content) {
        return;
      }

      // כל פריט רשימה (listItem) מכיל לרוב פסקה (paragraph)
      // ויכול להכיל גם רשימה מקוננת (nested list)
      const paragraphNode = listItem.content.find((c) => c.type === "paragraph");
      const nestedListNodes = listItem.content.filter(
        (c) => c.type === "orderedList" || c.type === "bulletList" || c.type === "taskList"
      );

      // 1. עבד את הפסקה של פריט הרשימה
      if (paragraphNode) {
        // חלץ את כל ה-TextRuns מהפסקה
        const textRuns = renderTextRunsFromContent(paragraphNode.content || []);

        if (listType === "taskList") {
          // --- טיפול מיוחד ב-TASK LIST ---
          const isChecked = listItem.attrs?.checked === true;
          const symbolChar = isChecked ? "F0FE" : "F071"; // F0FE = מסומן, F071 = ריק

          // TODO: add strike through text runs
          // הוסף קו חוצה אם התיבה מסומנת
          // const styledTextRuns = isChecked
          //   ? textRuns.map((run) => {
          //       // ניגש לאובייקט האפשרויות הפנימי של ה-TextRun
          //       const options = (run as any).options;
          //       // יוצר TextRun חדש עם אותן אפשרויות + קו חוצה
          //       return new TextRun({ ...options, strike: true });
          //     })
          //   : textRuns;

          paragraphs.push(
            new Paragraph({
              children: [
                new SymbolRun(symbolChar), // הוספת ה-Symbol
                new TextRun({ text: "\t" }), // הוספת טאב לריווח
                ...textRuns,
              ],
              bidirectional: true,
              spacing: { after: 100 },
              // הגדר הזחה ידנית עבור רשימת משימות
              indent: {
                left: convertInchesToTwip(0.5 + level * 0.5),
                hanging: convertInchesToTwip(0.25),
              },
            })
          );
        } else {
          // --- טיפול ב-ORDERED LIST ו-BULLET LIST ---
          paragraphs.push(
            new Paragraph({
              children: textRuns,
              bidirectional: true,
              // זוהי הדרך הנכונה להגדיר רשימה:
              numbering: {
                reference: listReference, // הפניה להגדרה הגלובלית
                level: level, // שימוש ברמת הקינון הנוכחית
              },
            })
          );
        }
      }

      // 2. עבד רשימות מקוננות (רקורסיה)
      nestedListNodes.forEach((nestedList) => {
        // קרא לפונקציה שוב עם הרשימה המקוננת ורמה + 1
        const nestedParagraphs = renderListNode(nestedList, level + 1);
        paragraphs.push(...nestedParagraphs);
      });
    });

    return paragraphs;
  };

  const renderQuoteNode = (node: JSONContent): Paragraph => {
    const textRuns = renderTextRunsFromContent(node.content || []);
    const direction = node.attrs?.dir;
    const { align, bidi } = direction ? {
      align: direction === "rtl" ? AlignmentType.RIGHT : AlignmentType.LEFT,
      bidi: direction === "rtl"
    } : getParagraphDirection(node);

    const indent = direction === "ltr" || (align === AlignmentType.LEFT && !direction)
      ? { left: 720, right: 720 }
      : { right: 720, left: 720 };

    const border = direction === "ltr" || (align === AlignmentType.LEFT && !direction)
      ? { left: { color: "CCCCCC", space: 1, style: "single" as const, size: 6 } }
      : { right: { color: "CCCCCC", space: 1, style: "single" as const, size: 6 } };

    return new Paragraph({
      children: textRuns,
      alignment: align,
      bidirectional: bidi,
      spacing: {
        before: 200,
        after: 200,
      },
      indent: indent,
      border: border,
    });
  };

  const renderTableNode = (node: JSONContent): Table => {
    const rows = node.content || [];
    const tableRows: TableRow[] = [];

    rows.forEach((rowNode) => {
      if (rowNode.type === "tableRow") {
        const cells = rowNode.content || [];
        const tableCells: TableCell[] = [];

        cells.forEach((cellNode) => {
          if (cellNode.type === "tableCell" || cellNode.type === "tableHeader") {
            // Extract text content from cell
            const cellContent = cellNode.content || [];
            const cellParagraphs: Paragraph[] = [];

            cellContent.forEach((cellContentNode) => {
              if (cellContentNode.type === "paragraph") {
                const textRuns = renderTextRunsFromContent(cellContentNode.content || []);
                const { align, bidi } = getParagraphDirection(cellContentNode);

                cellParagraphs.push(
                  new Paragraph({
                    children: textRuns.length > 0 ? textRuns : [new TextRun({ text: "" })],
                    alignment: align,
                    bidirectional: bidi,
                  })
                );
              }
            });

            // If no paragraphs were created, add an empty one
            if (cellParagraphs.length === 0) {
              cellParagraphs.push(new Paragraph({ children: [new TextRun({ text: "" })] }));
            }

            const isHeader = cellNode.type === "tableHeader";
            tableCells.push(
              new TableCell({
                children: cellParagraphs,
                width: {
                  size: 100 / cells.length,
                  type: WidthType.PERCENTAGE,
                },
                shading: isHeader
                  ? {
                      fill: "E0E0E0",
                    }
                  : undefined,
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1, color: "999999" },
                  bottom: { style: BorderStyle.SINGLE, size: 1, color: "999999" },
                  left: { style: BorderStyle.SINGLE, size: 1, color: "999999" },
                  right: { style: BorderStyle.SINGLE, size: 1, color: "999999" },
                },
              })
            );
          }
        });

        if (tableCells.length > 0) {
          tableRows.push(new TableRow({ children: tableCells }));
        }
      }
    });

    return new Table({
      rows: tableRows,
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
    });
  };

  const parseNodeToWord = (node: JSONContent): Paragraph | Paragraph[] | Table => {
    switch (node.type) {
      case "paragraph":
        return renderParagraphNode(node);
      case "heading":
        return renderHeadingNode(node);
      case "orderedList":
      case "bulletList":
      case "taskList":
        return renderListNode(node);
      case "blockquote":
        return renderQuoteNode(node);
      case "horizontalRule":
        return new Paragraph({
          children: [new TextRun({ text: "─".repeat(50) })],
          alignment: AlignmentType.CENTER,
          spacing: { before: 200, after: 200 },
        });
      case "table":
        return renderTableNode(node);
      default:
        return new Paragraph({
          children: [new TextRun({ text: "שגיאה בעיבוד התוכן" })],
          bidirectional: true,
        });
    }
  };

  const generateWordDocument = async () => {
    setIsGenerating(true);

    if (DOCX_DEBUG) {
      console.log("[DOCX_DEBUG] === DOCX Generation Start ===");
      console.log("[DOCX_DEBUG] fileName:", fileName);
      console.log("[DOCX_DEBUG] editorState node count:", editorState.content?.length);
      console.log("[DOCX_DEBUG] editorState full JSON:", JSON.stringify(editorState));

      Sentry.addBreadcrumb({
        category: "docx-generation",
        message: "DOCX generation started",
        data: {
          fileName,
          nodeCount: editorState.content?.length,
          nodeTypes: editorState.content?.map(n => n.type),
          userAgent: navigator.userAgent,
        },
        level: "info",
      });

      // Log each paragraph's text content and direction decisions
      editorState.content?.forEach((node, i) => {
        if (node.content) {
          const texts = node.content
            .filter(c => c.type === "text" && c.text)
            .map(c => c.text);
          const fullText = texts.join("");

          if (fullText) {
            const segments = splitTextByLanguage(fullText);
            const { align, bidi } = getParagraphDirection(node);

            Sentry.addBreadcrumb({
              category: "docx-debug",
              message: `Node[${i}] type=${node.type}`,
              data: {
                fullText: fullText.slice(0, 200),
                textCount: texts.length,
                segments: segments.map(s => ({ text: s.text.slice(0, 50), lang: s.lang })),
                paragraphAlign: align,
                paragraphBidi: bidi,
              },
              level: "debug",
            });
          }
        }
      });
    }

    try {
      const children: (Paragraph | Table)[] = [];

      // Parse all nodes from editor state
      editorState.content?.forEach((node) => {
        const parsedNode = parseNodeToWord(node);
        if (Array.isArray(parsedNode)) {
          children.push(...parsedNode);
        } else {
          children.push(parsedNode);
        }
      });

      // Detect document language from content
      const docLang = detectDocumentLanguage(editorState);

      if (DOCX_DEBUG) {
        console.log("[DOCX_DEBUG] Document language detected:", docLang);
      }

      // Create the document
      const doc = new Document({
        styles: {
          default: {
            document: {
              run: {
                font: {
                  ascii: "Arial",
                  hAnsi: "Arial",
                  eastAsia: "Arial",
                  cs: "Arial",
                },
                language: {
                  value: docLang,
                  bidirectional: docLang,
                },
              },
            },
          },
        },
        numbering,
        sections: [
          {
            properties: {},
            footers: {
              default: new Footer({
                children: [
                  new Paragraph({
                    children: [new TextRun({ text: fileName, size: 16, rightToLeft: docLang === 'he-IL' })],
                    alignment: AlignmentType.CENTER,
                    bidirectional: docLang === 'he-IL',
                    spacing: {
                      before: 400,
                    },
                  }),
                ],
              }),
            },
            children: children,
          },
        ],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (DOCX_DEBUG) {
        console.log("[DOCX_DEBUG] === DOCX Generation Complete ===");
        console.log("[DOCX_DEBUG] Total children (paragraphs/tables):", children.length);

        Sentry.captureMessage("DOCX generated successfully", {
          level: "info",
          tags: { feature: "docx-generation", debug: "true" },
          extra: {
            fileName,
            childrenCount: children.length,
            nodeCount: editorState.content?.length,
            userAgent: navigator.userAgent,
            // Capture first 10KB of editor state for debugging word order
            editorStateSnapshot: JSON.stringify(editorState).slice(0, 10000),
          },
        });
      }

      toast.success("Word הורד בהצלחה");
    } catch (error) {
      console.error("Error generating Word document:", error);

      if (DOCX_DEBUG) {
        Sentry.captureException(error, {
          tags: { feature: "docx-generation", debug: "true" },
          extra: {
            fileName,
            userAgent: navigator.userAgent,
            editorStateSnapshot: JSON.stringify(editorState).slice(0, 10000),
          },
        });
      }

      toast.error("שגיאה ביצירת ה-Word");
    } finally {
      setIsGenerating(false);
    }
  };

  const label = isGenerating ? "יוצר Word..." : "הורד כWord";

  return (
    <Button variant="outline" size="sm" onClick={generateWordDocument} disabled={isGenerating || disabledFromParent}>
      {isGenerating ? <Spinner className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
      {label}
    </Button>
  );
};

export default DownloadWordButton;
